import React, { useEffect, useRef, useState } from 'react';
import { Users, Target, Mountain, BarChart3, TrendingUp, PieChart, Activity } from 'lucide-react';

const ABOUT_IMG = "https://images.unsplash.com/photo-1758691737467-fe12934ddc58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwdGVhbSUyMGNvbGxhYm9yYXRpbmclMjBvZmZpY2V8ZW58MHx8fHwxNzc2Mjk2MDMwfDA&ixlib=rb-4.1.0&q=85&w=800";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_research-hub-218/artifacts/lk2wm99x_Skoop%21%20Logo%20-%202026.jpg";

/* ── Animated 3D data graphic with pie charts ── */
function DataGraphic3D() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: '94%', label: 'Satisfaction', icon: <TrendingUp size={14} />, color: '#166534', bg: 'rgba(22,101,52,0.08)' },
    { value: '2.4K', label: 'Responses', icon: <Users size={14} />, color: '#38BDF8', bg: 'rgba(56,189,248,0.1)' },
    { value: '87', label: 'NPS Score', icon: <BarChart3 size={14} />, color: '#DC2626', bg: 'rgba(220,38,38,0.08)' },
  ];

  /* Pie data for each engagement category */
  const pies = [
    { label: 'Events', pct: 82, color: '#0A192F', delay: 0.6 },
    { label: 'Dining', pct: 74, color: '#166534', delay: 0.85 },
    { label: 'Recreation', pct: 91, color: '#38BDF8', delay: 1.1 },
    { label: 'Lodging', pct: 68, color: '#DC2626', delay: 1.35 },
  ];

  const slices = [
    { label: 'Surveys', color: '#0A192F', pct: '55%' },
    { label: 'Panels', color: '#38BDF8', pct: '25%' },
    { label: 'Events', color: '#166534', pct: '20%' },
  ];

  /* SVG arc for a percentage (0-100) on a circle of radius r */
  const describeArc = (pct, r) => {
    const circumference = 2 * Math.PI * r;
    return { dash: (pct / 100) * circumference, gap: circumference };
  };

  return (
    <div ref={ref} style={{ perspective: '1200px' }}>
      <div
        className="neo-card p-8 bg-white relative overflow-hidden"
        style={{
          transform: visible ? 'rotateY(-2deg) rotateX(1deg)' : 'rotateY(-8deg) rotateX(6deg)',
          transition: 'transform 0.8s cubic-bezier(0.22,1,0.36,1)',
          transformStyle: 'preserve-3d',
          boxShadow: visible
            ? '6px 6px 0px 0px #0A192F, 12px 16px 40px rgba(10,25,47,0.15)'
            : '4px 4px 0px 0px #0A192F',
        }}
      >
        {/* Grid bg */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(0deg,#0A192F 0px,transparent 1px,transparent 24px),repeating-linear-gradient(90deg,#0A192F 0px,transparent 1px,transparent 24px)' }} />
        <div className="relative" style={{ transformStyle: 'preserve-3d' }}>

          {/* Header */}
          <div
            className="flex items-center justify-between mb-6"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0) translateZ(0)' : 'translateY(-40px) translateZ(60px)',
              transition: 'all 0.6s cubic-bezier(0.22,1,0.36,1) 0.1s',
            }}
          >
            <div className="flex items-center gap-2">
              <Activity size={18} className="text-[#38BDF8]" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>Research Insights</span>
            </div>
            <span className="text-xs font-mono text-[#475569]">Live Data</span>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {stats.map((stat, i) => (
              <div
                key={i}
                className="text-center p-3 rounded-xl border border-[#0A192F]/10 hover:scale-105 transition-transform duration-200 cursor-default"
                style={{
                  background: stat.bg,
                  opacity: visible ? 1 : 0,
                  transform: visible
                    ? 'translateY(0) translateZ(0) rotateX(0)'
                    : 'translateY(-60px) translateZ(80px) rotateX(-15deg)',
                  transition: `all 0.7s cubic-bezier(0.22,1,0.36,1) ${0.2 + i * 0.12}s`,
                  boxShadow: visible ? '0 4px 12px rgba(10,25,47,0.08)' : 'none',
                }}
              >
                <div className="flex items-center justify-center gap-1 mb-1" style={{ color: stat.color }}>{stat.icon}</div>
                <p className="text-2xl font-black text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</p>
                <p className="text-[10px] font-semibold text-[#475569] uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Pie charts – each pie drops in individually */}
          <div className="mb-6">
            <p
              className="text-xs font-bold text-[#0A192F] mb-4"
              style={{
                fontFamily: 'Outfit, sans-serif',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(-20px)',
                transition: 'all 0.5s ease 0.5s',
              }}
            >
              Community Engagement
            </p>
            <div className="grid grid-cols-4 gap-4">
              {pies.map((pie, i) => {
                const r = 16;
                const { dash, gap } = describeArc(pie.pct, r);
                return (
                  <div
                    key={i}
                    className="flex flex-col items-center"
                    style={{
                      opacity: visible ? 1 : 0,
                      transform: visible
                        ? 'translateY(0) translateZ(0) scale(1)'
                        : 'translateY(-50px) translateZ(50px) scale(0.6)',
                      transition: `all 0.7s cubic-bezier(0.22,1,0.36,1) ${pie.delay}s`,
                    }}
                  >
                    <div className="relative w-20 h-20" style={{ filter: 'drop-shadow(0 3px 6px rgba(10,25,47,0.1))' }}>
                      <svg viewBox="0 0 40 40" className="w-full h-full" style={{
                        transform: visible ? 'rotate(-90deg)' : 'rotate(-180deg)',
                        transition: `transform 0.8s cubic-bezier(0.22,1,0.36,1) ${pie.delay + 0.1}s`,
                      }}>
                        <circle cx="20" cy="20" r={r} fill="none" stroke="#F1F5F9" strokeWidth="5" />
                        <circle
                          cx="20" cy="20" r={r} fill="none"
                          stroke={pie.color} strokeWidth="5" strokeLinecap="round"
                          strokeDasharray={visible ? `${dash} ${gap - dash}` : `0 ${gap}`}
                          style={{ transition: `stroke-dasharray 1s cubic-bezier(0.22,1,0.36,1) ${pie.delay + 0.2}s` }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-sm font-black text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>{pie.pct}%</span>
                      </div>
                    </div>
                    <span className="text-[10px] font-semibold text-[#475569] mt-2 uppercase tracking-wider">{pie.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom donut – spins in */}
          <div
            className="flex items-center gap-6"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0) translateZ(0) scale(1)' : 'translateY(-50px) translateZ(60px) scale(0.8)',
              transition: 'all 0.8s cubic-bezier(0.22,1,0.36,1) 1.6s',
            }}
          >
            <div className="relative w-24 h-24 shrink-0" style={{ filter: 'drop-shadow(0 4px 8px rgba(10,25,47,0.12))' }}>
              <svg viewBox="0 0 36 36" style={{
                transform: visible ? 'rotate(-90deg)' : 'rotate(-180deg)',
                transition: 'transform 1.2s cubic-bezier(0.22,1,0.36,1) 1.7s',
              }} className="w-full h-full">
                <circle cx="18" cy="18" r="14" fill="none" stroke="#F1F5F9" strokeWidth="4.5" />
                <circle cx="18" cy="18" r="14" fill="none" stroke="#0A192F" strokeWidth="4.5"
                  strokeDasharray={visible ? "55 45" : "0 100"} strokeDashoffset="0" strokeLinecap="round"
                  style={{ transition: 'stroke-dasharray 1s ease 1.8s' }} />
                <circle cx="18" cy="18" r="14" fill="none" stroke="#38BDF8" strokeWidth="4.5"
                  strokeDasharray={visible ? "25 75" : "0 100"} strokeDashoffset="-55" strokeLinecap="round"
                  style={{ transition: 'stroke-dasharray 1s ease 2.0s' }} />
                <circle cx="18" cy="18" r="14" fill="none" stroke="#166534" strokeWidth="4.5"
                  strokeDasharray={visible ? "20 80" : "0 100"} strokeDashoffset="-80" strokeLinecap="round"
                  style={{ transition: 'stroke-dasharray 1s ease 2.2s' }} />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <PieChart size={18} className="text-[#0A192F]" />
              </div>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-2">
              {slices.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateX(0)' : 'translateX(20px)',
                    transition: `all 0.5s ease ${1.9 + i * 0.15}s`,
                  }}
                >
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color, boxShadow: `0 2px 4px ${item.color}40` }} />
                  <span className="text-xs text-[#475569] font-medium">{item.label} <span className="font-bold text-[#0A192F]">{item.pct}</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-[#0A192F] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src={LOGO_URL} alt="SKOOP! Logo" className="h-28 w-auto mx-auto mb-8 animate-fade-up p-2 bg-white/10 rounded-2xl backdrop-blur-sm" style={{ filter: 'drop-shadow(0 0 30px rgba(56,189,248,0.3))' }} />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6 animate-fade-up animate-delay-100" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Who We Are
          </h1>
          <p className="text-base text-white/90 max-w-3xl mx-auto mb-6 animate-fade-up animate-delay-200 italic" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="font-bold text-[#38BDF8]">Our Mission:</span> To quickly, but thoroughly and economically help our clients obtain the right information in which they can comfortably rely on to make data-based decisions.
          </p>
          <p className="text-lg text-white/70 max-w-2xl mx-auto animate-fade-up animate-delay-300" style={{ fontFamily: 'Manrope, sans-serif' }}>
            A community-powered research platform based in the heart of Colorado.
          </p>
        </div>
      </section>

      {/* Main content */}
      <section className="py-20 sm:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A192F] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Community Pulse,<br />Community Powered
              </h2>
              <div className="space-y-5 text-base text-[#475569] leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <p>
                  SKOOP! is an information platform powered by Intercept Insight, LLC, a research firm based in Avon, CO designed to keep the community's pulse on the community.
                </p>
                <p>
                  We offer our community and those visiting our community the opportunity to provide feedback through routine studies on topics of interest that impact the community.
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <DataGraphic3D />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              What Drives Us
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Users size={28} />, title: 'Community First', desc: 'Every study starts and ends with the community in mind.' },
              { icon: <Target size={28} />, title: 'Precision', desc: 'Reliable data collection methods that produce actionable results.' },
              { icon: <Mountain size={28} />, title: 'Colorado Rooted', desc: 'Proudly based in Avon, CO — we know mountain communities.' },
              { icon: <BarChart3 size={28} />, title: 'Impact', desc: 'Turning feedback into decisions that improve everyday life.' },
            ].map((item, i) => (
              <div key={i} className="neo-card p-8 text-center">
                <div className="w-14 h-14 rounded-xl bg-[#166534] text-white flex items-center justify-center mb-5 mx-auto">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-[#0A192F] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Powered by */}
      <section className="py-16 bg-[#F8FAFC] border-t-2 border-[#0A192F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <img src={LOGO_URL} alt="SKOOP!" className="h-20 w-auto" />
            <div className="text-center sm:text-left">
              <p className="text-sm font-bold text-[#0A192F] uppercase tracking-widest" style={{ fontFamily: 'Outfit, sans-serif' }}>Powered by</p>
              <p className="text-xl font-bold text-[#0A192F]" style={{ fontFamily: 'Outfit, sans-serif' }}>Intercept Insight, LLC</p>
              <p className="text-sm text-[#475569]">Avon, Colorado</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
